/**
 * QrCard — the certificado QR's decoded destination + its Registro de URLs
 * oficiales classification (issue #261, ADR 0006), read from the latest run's
 * schemaless `checks` summary (served by the history endpoint).
 *
 * The URL renders as a clickable link (new tab) ONLY for the registro-vetted
 * classifications (`qrUrlIsLinkable`); a dominio sospechoso — or an
 * unclassified URL — stays plain TEXT: Vera reads it locally/offline and the
 * reviewer should eyeball it, not visit it (ADR 0006). No QR / undecodable /
 * pre-#258 records get a neutral absent line, never a warning.
 */

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Card, CardContent, Chip, Link, Stack, Typography } from "@mui/material";

import { extractQrSummary, QR_BADGE, qrUrlIsLinkable } from "./qrOfficial";

export function QrCard({
  checks,
  loading = false,
  unavailable = false,
}: {
  /** The latest run's `checks` (`layer_summary`); null/undefined renders the
   * neutral absent state ("sin QR"). */
  checks: unknown;
  loading?: boolean;
  /** True when the summary could not be FETCHED — rendered as its own neutral
   * line, never as "sin QR": a fetch failure is not a fact about the QR. */
  unavailable?: boolean;
}) {
  const { url, status } = extractQrSummary(checks);
  const badge = status ? QR_BADGE[status] : null;

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h3" gutterBottom>
          QR del certificado
        </Typography>

        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Consultando…
          </Typography>
        ) : unavailable ? (
          <Typography variant="body2" color="text.secondary">
            No se pudo consultar el resumen de verificaciones.
          </Typography>
        ) : url === null && badge === null ? (
          <Typography variant="body2" color="text.secondary">
            Sin QR con URL descifrable en el certificado.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {badge && (
              <Box>
                <Chip
                  label={badge.label}
                  color={badge.color}
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
            {url !== null && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Destino del QR
                </Typography>
                {qrUrlIsLinkable(status) ? (
                  <Link
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {url}
                  </Link>
                ) : (
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {url}
                  </Typography>
                )}
                {qrUrlIsLinkable(status) && (
                  <Box sx={{ mt: 1 }}>
                    <Button
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      variant="outlined"
                      endIcon={<OpenInNewIcon />}
                    >
                      Ir al enlace
                    </Button>
                  </Box>
                )}
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              Leído localmente del QR — Vera no visita la URL.
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
